import { get, debounce } from 'lodash';
import { createAction } from '@wix/communities-blog-client-common';
import { type CommonThunkAction } from '@app/types/common-thunk-action.type';
import { createPromisifiedAction } from '../actions-promisifier/create-promisified-action';
import { appendOriginInBackend, appendAuthHeaders } from '../controller/helpers';

const fetchMentionsRequest = createAction('mentions/FETCH_REQUEST');
const fetchMentionsSuccess = createAction('mentions/FETCH_SUCCESS');
const fetchMentionsFailure = createAction('mentions/FETCH_FAILURE');

type MembersQueryResponse = {
  members: {
    profile: {
      nickname: string;
      slug: string;
      photo?: { url?: string };
    };
  }[];
};

const fetchMentions =
  (query: string): CommonThunkAction<any> =>
  async (dispatch, _getState, { httpClient, errorHandler, platformApi }) => {
    dispatch(fetchMentionsRequest({ query }));
    const { withErrorHandler } = errorHandler;
    const body = {
      fieldSet: 'PUBLIC',
      query: {
        filter: {
          'profile.nickname': { $contains: query },
          privacyStatus: 'PUBLIC',
        },
      },
      paging: {
        limit: 10,
        offset: 0,
      },
    };
    try {
      const { data } = await withErrorHandler(
        () =>
          httpClient.post<MembersQueryResponse>(
            `${appendOriginInBackend({
              platformApi,
              baseUrl: '/_api/members/v1',
            })}/members/query`,
            body,
            {
              headers: appendAuthHeaders(platformApi),
            },
          ),
        { errorCodesMap: {} },
      );
      const mentions = data.members.map((member) => ({
        name: member.profile.nickname,
        slug: member.profile.slug,
        avatar: get(member, 'profile.photo.url'),
      }));

      dispatch(fetchMentionsSuccess(mentions));
      return mentions;
    } catch (error) {
      dispatch(fetchMentionsFailure());
      return [];
    }
  };

const createDebouncedFetchMentions = () => {
  const debouncedFetchMentions = debounce(
    (dispatch: any, query: string, cb: (res: any) => void) =>
      dispatch(fetchMentions(query)).then(cb),
    500,
  );
  return (query: string) => (dispatch: any) =>
    new Promise((resolve) => debouncedFetchMentions(dispatch, query, resolve));
};

export const fetchMentionsPromisified = createPromisifiedAction(
  createDebouncedFetchMentions(),
  (res) => res,
);
